import type { TFunction } from 'i18next';
import type { MeasureArea, Proposal, ProposalError, WorkEnvironmentSurvey } from '../../api/workEnvironmentTypes';
import { WorkEnvironmentSurveyStatus } from '../../api/workEnvironmentTypes';
import type { FieldError } from './assessmentView';
import { dayAndMonth, daysLeft } from './surveyProcessView';

/*
 * The limits are checked again in the API, which is the source of truth.
 * They are here so the form can count down while the person types.
 */
export const MAX_TITLE_LENGTH = 80;

const MEASURE_STATUSES: WorkEnvironmentSurveyStatus[] = [
  WorkEnvironmentSurveyStatus.ACTION_PROPOSAL_OPEN,
  WorkEnvironmentSurveyStatus.ACTION_VOTING_OPEN,
];

/** Surveys with an HMS risk follow the new measures. Older ones keep the old dashboard. */
export const showsMeasuresPage = (survey: Pick<WorkEnvironmentSurvey, 'status' | 'riskCalculatedAt'>) =>
  Boolean(survey.riskCalculatedAt) && MEASURE_STATUSES.includes(survey.status);

/** The areas that need measures first, then the others */
export const areasByNeed = (areas: MeasureArea[]) => ({
  needing: areas.filter((area) => area.needsMeasures),
  others: areas.filter((area) => !area.needsMeasures),
});

export const proposalsIn = <T extends Pick<Proposal, 'factorKey'>>(proposals: T[], areaKey: string | null) =>
  areaKey ? proposals.filter((proposal) => proposal.factorKey === areaKey) : proposals;

/** «Teammedlem (anonym)», or who proposed it when it was the leader */
export const proposalOrigin = (proposal: Pick<Proposal, 'fromLeader' | 'createdBy'>, t: TFunction) =>
  proposal.fromLeader
    ? t('workEnvironmentMeasures.origin.leader', { email: proposal.createdBy ?? '' })
    : t('workEnvironmentMeasures.origin.team');

export const PROPOSAL_FIELDS: Record<ProposalError, string> = {
  area: 'proposal-area',
  title: 'proposal-title',
  titleLength: 'proposal-title',
  reasonLength: 'proposal-reason',
};

export const proposalFieldErrors = (errors: ProposalError[], t: TFunction): FieldError[] =>
  errors.map((error) => ({
    fieldId: PROPOSAL_FIELDS[error],
    message: t(`workEnvironmentMeasures.errors.${error}`, { max: MAX_TITLE_LENGTH }),
  }));

export const charactersLeft = (title: string) => MAX_TITLE_LENGTH - title.length;

/** Choosing a picked proposal takes it back. Beyond the limit nothing changes, and the page says why. */
export const togglePick = (picks: number[], id: number, max: number) =>
  picks.includes(id) ? picks.filter((pick) => pick !== id) : picks.length < max ? [...picks, id] : picks;

/** The link the team uses for both proposing and pointing out measures */
export const teamLink = (origin: string, uuid: string) => `${origin}/work-environment-actions/${uuid}`;

/** The proposals grouped by area, in the order of the areas, leaving out areas without proposals */
export const proposalsByArea = <T extends Pick<Proposal, 'factorKey'>>(areas: MeasureArea[], proposals: T[]) =>
  areas
    .map((area) => ({ area, proposals: proposalsIn(proposals, area.key) }))
    .filter((group) => group.proposals.length > 0);

/**
 * The team was told how long they have. Closing the step before that breaks what they were
 * told, so the team leader sees how many days early it is.
 */
export const closeEarlyText = (
  step: 'propose' | 'highlight',
  deadline: string | null,
  t: TFunction,
  language: string,
  now = new Date(),
) => {
  const days = deadline ? daysLeft(deadline, now) : 0;
  return deadline && days > 0
    ? t(`workEnvironmentMeasures.next.${step}.early`, { date: dayAndMonth(deadline, language), count: days })
    : null;
};

const CLOSED_CODES = ['proposalsClosed', 'highlightsClosed'];

/** The deadline may close the step while the team leader does, and then there is nothing to close */
export const closeErrorKey = (error: unknown) => {
  const code = (error as { response?: { data?: { code?: string } } }).response?.data?.code;
  return CLOSED_CODES.includes(code ?? '') ? 'alreadyClosed' : 'failed';
};
